import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Scale } from 'lucide-react';

// 14/10 (C-505) — LE BOT RAPPORTE-T-IL PLUS QU'IL NE COÛTE ?
// Marlon : « j'aimerais avoir le déficit, c'est-à-dire voir si je suis en positif du fait du bot
// actuellement ou si je suis en négatif. Est-ce que le bot fait gagner plus qu'il ne consomme ? »
// Lecture seule de `/api/hera/bilan` : ventes signées « 🤖 Bot HERA » − coût estimé du bot, sur les
// mêmes journées que le relevé de coût du bot (journées UTC = 02:00 → 02:00 heure de Paris).

const eur = (v) => `${(Number(v) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const usd = (v) => `${(Number(v) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;

export default function BilanBot() {
  const [b, setB] = useState(null);
  const [erreur, setErreur] = useState('');
  useEffect(() => {
    api.get('/hera/bilan').then(({ data }) => setB(data)).catch((e) => setErreur(e?.response?.data?.detail || e.message || 'indisponible'));
  }, []);

  if (erreur && !b) return <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.06] p-4 mb-4 text-[13px] text-amber-100/90" data-testid="bilan-bot">Bilan du bot indisponible pour l'instant ({erreur}).</div>;
  if (!b) return <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 mb-4 text-sm text-slate-500" data-testid="bilan-bot">Calcul du bilan du bot…</div>;

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-4" data-testid="bilan-bot">
      <h3 className="font-semibold text-white flex items-center gap-2 mb-3"><Scale size={16} className="text-indigo-400" /> Le bot te rapporte-t-il plus qu'il ne coûte ?</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {(b.periodes || []).map((p) => {
          const connu = p.solde_eur !== null && p.solde_eur !== undefined;
          const positif = connu && p.solde_eur >= 0;
          return (
            <div key={p.cle} data-testid={`bilan-bot-${p.cle}`}
              className={`rounded-xl border p-4 ${!connu ? 'border-white/10 bg-white/[0.02]' : positif ? 'border-emerald-500/40 bg-emerald-500/[0.07]' : 'border-red-500/40 bg-red-500/[0.07]'}`}>
              <div className="text-[11px] text-slate-400">{p.libelle}</div>
              <div className={`text-2xl font-bold mt-1 ${!connu ? 'text-slate-400' : positif ? 'text-emerald-300' : 'text-red-300'}`}>
                {connu ? `${positif ? '+' : '−'}${eur(Math.abs(p.solde_eur))}` : '—'}
              </div>
              <div className="text-[11px] mt-1 text-slate-400">{connu ? (positif ? 'le bot rapporte plus qu’il ne coûte' : 'le bot coûte plus qu’il ne rapporte') : 'coût ou taux indisponible'}</div>
              <div className="text-[12px] text-slate-300 mt-2 space-y-0.5">
                <div>Vendu par le bot : <b className="text-white">{eur(p.ca_bot_eur)}</b> <span className="text-slate-500">({p.ventes_bot} vente{p.ventes_bot > 1 ? 's' : ''})</span></div>
                <div>Coût du bot : <b className="text-white">{p.cout_eur !== null && p.cout_eur !== undefined ? eur(p.cout_eur) : '—'}</b>{p.cout_usd !== null && p.cout_usd !== undefined && <span className="text-slate-500"> ({usd(p.cout_usd)})</span>}</div>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
        <b className="text-slate-400">Vendu par le bot</b> = uniquement les ventes signées « 🤖 Bot HERA ». Une vente faite par une chatteuse reste à la chatteuse, même si le fan est en zone bot.
        {' '}<b className="text-slate-400">Coût</b> = estimation du bot sur ses appels à l'IA (pas la facture : elle se lit dans la console Anthropic),
        {b.taux_usd_eur ? <> convertie au taux de la Banque centrale européenne du {b.taux_date} : 1 $ = {String(b.taux_usd_eur).replace('.', ',')} €.</> : ' taux de conversion indisponible.'}
        {' '}Les journées vont de 02:00 à 02:00 (heure de Paris), comme le relevé de coût du bot.
      </p>
    </div>
  );
}
